import Phaser from 'phaser';

export interface CameraControllerOptions {
  /** World extents the camera is bounded to. */
  worldWidth: number;
  worldHeight: number;
  /** Viewport (design) width used to compute the fit-to-width minimum zoom. */
  viewWidth: number;
  /** Max zoom-in factor (default 2.5). Min zoom is always fit-to-width. */
  maxZoom?: number;
  /** Where to anchor the initial vertical scroll: top (0) or bottom of world. */
  startAt?: 'top' | 'bottom';
  /** Called on a tap (pointer down→up with no drag/pinch), in WORLD coords. */
  onTap: (worldX: number, worldY: number) => void;
}

/**
 * Drag-to-pan / pinch- & wheel-to-zoom camera rig, shared by `BattleScene` and
 * `WorldMapScene`. Both present the same gesture: a tall world panned and zoomed
 * inside a portrait viewport, with a tap distinguished from a drag so taps can
 * select (a build slot in battle, a map node on the world map).
 *
 * Min zoom is fit-to-width (you can't zoom out past the world's width); the
 * caller supplies the tap handler and where to start scrolled. Extracted from
 * the original inline BattleScene.setupCamera/setupInput so the behaviour is
 * defined once.
 */
export class CameraController {
  /** When false, all pan/zoom/tap input is ignored (e.g. during a cinematic). */
  enabled = true;
  private minZoom = 0.5;
  private maxZoom = 2.5;
  private dragging = false;
  private dragMoved = false;
  private pinching = false;
  private readonly dragLast = new Phaser.Math.Vector2();
  private pinchStartDist = 0;
  private pinchStartZoom = 1;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly opts: CameraControllerOptions,
  ) {
    this.maxZoom = opts.maxZoom ?? 2.5;
    this.setupCamera();
    this.setupInput();
  }

  private setupCamera(): void {
    const cam = this.scene.cameras.main;
    cam.setBounds(0, 0, this.opts.worldWidth, this.opts.worldHeight);
    const fitWidth = this.opts.viewWidth / this.opts.worldWidth;
    this.minZoom = fitWidth;
    cam.setZoom(fitWidth);
    cam.centerOnX(this.opts.worldWidth / 2);
    // Anchor vertically: battle opens at the top of the descent; the world map
    // opens at the bottom (Olympus, where the pilgrimage begins).
    if (this.opts.startAt === 'bottom') {
      cam.centerOnY(this.opts.worldHeight - this.scene.scale.height / 2 / cam.zoom);
    } else {
      cam.scrollY = 0;
    }
  }

  private setupInput(): void {
    const cam = this.scene.cameras.main;
    const input = this.scene.input;

    input.on('pointerdown', () => {
      if (!this.enabled) return;
      const p1 = input.pointer1;
      this.dragging = true;
      this.dragMoved = false;
      this.dragLast.set(p1.x, p1.y);
    });

    input.on('pointermove', () => {
      if (!this.enabled) return;
      const p1 = input.pointer1;
      const p2 = input.pointer2;
      if (p1.isDown && p2.isDown) {
        this.pinching = true;
        this.dragMoved = true;
        const dist = Phaser.Math.Distance.Between(p1.x, p1.y, p2.x, p2.y);
        if (this.pinchStartDist === 0) {
          this.pinchStartDist = dist;
          this.pinchStartZoom = cam.zoom;
        } else {
          const z = this.pinchStartZoom * (dist / this.pinchStartDist);
          cam.setZoom(Phaser.Math.Clamp(z, this.minZoom, this.maxZoom));
        }
        return;
      }
      this.pinchStartDist = 0;
      if (this.dragging && p1.isDown) {
        const dx = p1.x - this.dragLast.x;
        const dy = p1.y - this.dragLast.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) this.dragMoved = true;
        cam.scrollX -= dx / cam.zoom;
        cam.scrollY -= dy / cam.zoom;
        this.dragLast.set(p1.x, p1.y);
      }
    });

    input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (!this.enabled) return;
      const wasTap = this.dragging && !this.dragMoved && !this.pinching;
      this.dragging = false;
      if (!input.pointer2.isDown) this.pinchStartDist = 0;
      if (!input.pointer1.isDown && !input.pointer2.isDown) this.pinching = false;
      if (wasTap) {
        const w = cam.getWorldPoint(pointer.x, pointer.y);
        this.opts.onTap(w.x, w.y);
      }
    });

    input.on('wheel', (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
      if (!this.enabled) return;
      const z = cam.zoom * (dy > 0 ? 0.9 : 1.1);
      cam.setZoom(Phaser.Math.Clamp(z, this.minZoom, this.maxZoom));
    });
  }
}
